import React from 'react';
import { useAppContext } from '../Contexts/AppContext';
import Constants from '../Utilities/Constants';

/**
 * ProductImage Component
 * 
 * Displays product images with automatic fallback to site logo when:
 * 1. Product image URL is not defined
 * 2. Product image fails to load (404, network error, etc.)
 * 
 * Automatically uses site logo from AppContext and product name for alt text.
 * 
 * Props:
 * - product: Product object with image and productName properties
 * - className: CSS classes
 * - style: Additional styles
 * - asBackground: If true, renders as background-image div, otherwise as img tag
 */
const ProductImage = ({ 
    product, 
    className = "", 
    style = {}, 
    asBackground = false 
}) => {
    const { site } = useAppContext();
    
    // Wait for product to be fully loaded, but don't wait indefinitely for site
    if (!product || !product.productName) {
        return (
            <div 
                className={className} 
                style={{
                    backgroundColor: 'transparent',
                    ...style
                }}
            />
        ); // Show empty space until product is loaded
    }
    
    // Simple logic: product image if exists, else site logo (with fallback)
    const imageUrl = product.image 
        ? `${Constants.IMAGE_URL}${product.image}` 
        : (site?.logo ? `${Constants.IMAGE_URL}${site.logo}` : null);
    

    if (asBackground) {
        const backgroundStyle = {
            backgroundImage: imageUrl ? `url("${imageUrl}")` : 'none',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            ...style
        };

        return (
            <div 
                className={className} 
                style={backgroundStyle}
                title={product.productName}
            />
        );
    }

    return (
        <img 
            src={imageUrl}
            alt={product.productName}
            className={className}
            style={style}
        />
    );
}


export default ProductImage;